import { useSelector } from "react-redux";

import { Edit } from "@mui/icons-material";

import { Tooltip } from "components/Tooltip";
import {
  LineTableHeadingCell,
  LineTableCell,
  LineTableFooterCell,
} from "components/formFields/LineTableCell";

import { i18n } from "services/i18nService";
import { isPersistedRecord } from "services/utility/misc";

import { theme } from "SosTheme";

/**
 * Heading cell for the edit button column.
 * Only shown when transactionEditMode is "rowByRow" and record is persisted.
 */
export function EditLineButtonHeading({ recordId }) {
  const transactionEditMode = useSelector(
    (state) => state.userCompanySettings.settings.transactionEditMode
  );

  if (transactionEditMode !== "rowByRow" || !isPersistedRecord(recordId)) {
    return null;
  }

  return (
    <LineTableHeadingCell sx={{ displayPrint: "none" }}>
      <div
        style={{
          position: "absolute",
          right: "100%",
          width: "1.3em",
          zIndex: theme.zIndex.stickyHeader,
          padding: "2.5rem 0.75rem 0",
          marginBottom: "3rem",
          backgroundColor: theme.palette.componentBackground,
        }}
      ></div>
    </LineTableHeadingCell>
  );
}

/**
 * Edit button for making a read-only line editable.
 * Only shown when transactionEditMode is "rowByRow", the line is read-only, and record is persisted.
 */
export function EditLineButton({
  onClick,
  lineIndex,
  recordId,
  lineReadOnly = false,
}) {
  const transactionEditMode = useSelector(
    (state) => state.userCompanySettings.settings.transactionEditMode
  );

  if (transactionEditMode !== "rowByRow" || !isPersistedRecord(recordId)) {
    return null;
  }

  return (
    <LineTableCell sx={{ displayPrint: "none" }}>
      <Tooltip
        placement="top-start"
        title={
          lineReadOnly
            ? i18n("tooltip.EditThisLine")
            : i18n("tooltip.LineIsAlreadyEditable")
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
            cursor: lineReadOnly ? "pointer" : "default",
            opacity: lineReadOnly ? 1 : 0.3,
          }}
          onClick={() => lineReadOnly && onClick(lineIndex)}
        >
          <Edit
            sx={{
              fontSize: "1.125rem",
              color: lineReadOnly ? "primary.main" : "text.disabled",
            }}
          />
        </div>
      </Tooltip>
    </LineTableCell>
  );
}

/**
 * Footer cell for the edit button column.
 * Only shown when transactionEditMode is "rowByRow" and record is persisted.
 */
export function EditLineButtonFooter({ recordId }) {
  const transactionEditMode = useSelector(
    (state) => state.userCompanySettings.settings.transactionEditMode
  );

  if (transactionEditMode !== "rowByRow" || !isPersistedRecord(recordId)) {
    return null;
  }

  return <LineTableFooterCell sx={{ border: "none", displayPrint: "none" }} />;
}
